// dependencies
import { differenceInCalendarMonths, startOfMonth, subYears } from "date-fns";
import React, { useMemo } from "react";

import Text from "../../../../../../../components/Text";

// schema
import { Direction } from "../../KPIs/schema";
import Barchart from "../Barchart";
// components
import PercentChangeIndicator from "../PercentChangeIndicator";
// css
import css from "./SatisfactionMeter.module.scss";

export interface SatisfactionMeterProps {
	monthlyData?: { time_bucket: Date; average: number }[];
	overallAverage?: number;
}

const SatisfactionMeter = (props: SatisfactionMeterProps) => {
	const { monthlyData = [], overallAverage = 0 } = props;

	// derive the data we need from the useOverallSummary response
	const [latestAverage, earliestAverage]: [number, number] = useMemo(() => {
		if (overallAverage) {
			const earliest =
				monthlyData.filter((point) => point.time_bucket >= subYears(new Date(), 1)).reverse()[0]?.average || 0;
			const latest = monthlyData[0]?.average || 0;
			return [latest, earliest];
		} else {
			return [0, 0];
		}
	}, [monthlyData]);
	/**
	 * barchartMonthlyData is an Array of averages for each month over the last year
	 * the most recent month is at index 11, the oldest month is at index 0
	 * this puts the most recent months at the right side of the barchart
	 */
	const barchartMonthlyData: number[] = useMemo(() => {
		// object with 12 keys, using indexes 11 - 0 as keys to represent the number of months ago
		const barchartData = Object.fromEntries([...[...Array(12)].map((_, i) => [i, 0])]);
		// now loop through the monthlyData and add the average to the barchartData object
		if (monthlyData.length) {
			monthlyData.forEach((summary) => {
				const monthsAgo = differenceInCalendarMonths(
					startOfMonth(new Date()),
					startOfMonth(new Date(summary.time_bucket))
				);
				if (monthsAgo < 12) {
					// if the month already has data, average it with the new data
					if (barchartData[monthsAgo.toString()] > 0) {
						// new average = current average + this average / 2
						barchartData[monthsAgo.toString()] = (barchartData[monthsAgo] + summary.average) / 2;
					}
					// otherwise just add the new data, replacing the 0
					else {
						barchartData[monthsAgo.toString()] = summary.average;
					}
				}
			});
		}

		// returns data at indexes 11 - 0 of the monthlyData object
		// with indexes representing the number of moths ago
		return [...Array(12)].map((_, i) => {
			return barchartData[(11 - i).toString()];
		});
	}, [monthlyData]);

	const [direction, percentChange]: [Direction, number] = useMemo(() => {
		if (earliestAverage > 0 && latestAverage > 0) {
			const change = (latestAverage - earliestAverage) / earliestAverage;
			if (change > 0) return ["positive", change];
			if (change < 0) return ["negative", change];
		}
		return ["zero", 0];
	}, [latestAverage, earliestAverage]);

	return (
		<div className={css.summary} data-testid="satisfaction-range-meter">
			<>
				<Text size="heading-1" weight="bold" testID="score">
					{overallAverage > 0 ? overallAverage.toFixed(1) : 0}
				</Text>
				<span className={css.percentChange} data-testid="percent-change">
					<PercentChangeIndicator
						tooltipText="% change during the selected time period."
						percentChange={Math.abs(percentChange) * 100}
						direction={direction}
					/>
				</span>
				<span className={css.barchart} data-testid="barchart">
					<Barchart direction={direction} monthlyData={barchartMonthlyData} />
				</span>
			</>
		</div>
	);
};

export default SatisfactionMeter;
